import './GalleryUpload.scss';

import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { Image, Modal, notification, Upload, UploadFile } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useEffect, useState } from 'react';
//@ts-ignore
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';

import { queryClient } from '../../../../../App';
import Button from '../../../../../components/Button/Button';
import LoaderComponent from '../../../../../components/LoaderCompoment/LoaderComponent';
import { useDeleteUserPhotos } from '../../../../services/myProfile/useDeleteUserPhotos';
import {
  key as UserGalleryKey,
  useGetUserGallery,
} from '../../../../services/myProfile/useGetUserGallery';
import { useUpdateUserPhotos } from '../../../../services/myProfile/useUpdateUserPhotos';

const { Dragger } = Upload;

interface GalleryUploadType {
  userId: string;
}
interface GalleryImageType {
  id: number;
  name: string;
  thumbnail_url: string;
  url: string;
}

const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const beforeUploadImaage = (file: RcFile) => {
  const isJpgOrPng =
    file.type === 'image/jpeg' ||
    file.type === 'image/jpg' ||
    file.type === 'image/png' ||
    file.type === 'image/bmp' ||
    file.type === 'image/gif';
  if (!isJpgOrPng) {
    notification.error({
      message: 'You can only upload JPG/PNG/BMP/GIF file!',
    });
  }
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    notification.error({
      message: 'Image must smaller than 2MB!',
    });
  }
  return isJpgOrPng && isLt2M;
};

const GalleryUpload = ({ userId }: GalleryUploadType) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteImage, setDeleteImage] = useState<String>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { mutate: saveUserGalleryPhotos, isLoading } = useUpdateUserPhotos();
  const { mutate: deleteUserPhoto } = useDeleteUserPhotos();
  const { data: userGeneralData } = useGetUserGallery(userId);
  const userGalleryImages = userGeneralData?.gallery_images;
  let galleryPhotosNumber = userGeneralData ? userGeneralData?.gallery_images?.length : 0;
  const galleryPhotoLimit = 30;
  const BASE_URL = `${process.env.REACT_APP_API_URL}/`;

  const showModal = (image: any) => {
    setIsModalOpen(true);
    setDeleteImage(image);
  };

  const handleOk = () => {
    setIsModalOpen(false);
    //@ts-ignore
    deleteUserPhoto(deleteImage, {
      onSuccess: (res) => {
        queryClient.invalidateQueries(UserGalleryKey);
      },
    });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    fileList?.length > 0 &&
      saveUserGalleryPhotos(
        //@ts-ignore
        { userId, file: fileList, type: 'gallery_images' },
        {
          onSuccess: (res) => {
            setFileList([]);
            queryClient.invalidateQueries(UserGalleryKey);
          },
        },
      );
  }, [fileList, saveUserGalleryPhotos, userId]);

  const beforeUpload = (file: any, fileList: any) => {
    setFileList(fileList);
    return false;
  };

  const uploadGalleryImages = (data: any) => {
    saveUserGalleryPhotos(
      //@ts-ignore
      { userId, file: data.file, type: 'gallery_images' },
      {
        onSuccess: (res) => {
          setFileList([]);
          queryClient.invalidateQueries(UserGalleryKey);
        },
      },
    );
  };

  const beforeUploadImage = (file: RcFile) => {
    const isJpgOrPng =
      file.type === 'image/jpeg' ||
      file.type === 'image/jpg' ||
      file.type === 'image/png' ||
      file.type === 'image/bmp' ||
      file.type === 'image/gif';
    galleryPhotosNumber && galleryPhotosNumber++;
    if (!isJpgOrPng) {
      galleryPhotosNumber && galleryPhotosNumber--;
      notification.error({
        message: 'You can only upload JPG/PNG/BMP/GIF file!',
      });
    }

    const isLt2M = file.size / 1024 / 1024 < 10;
    if (!isLt2M) {
      galleryPhotosNumber && galleryPhotosNumber--;
      notification.error({
        message: `Image ${file.name} must be smaller than 10MB!`,
      });
    }

    let limitNumber = true;
    if (galleryPhotosNumber && galleryPhotosNumber > galleryPhotoLimit) {
      limitNumber = false;
      galleryPhotosNumber && galleryPhotosNumber--;
      notification.error({
        message: `${file.name} sorry, you reached the gallery limit (30). Please delete an existing photo to upload this image.`,
      });
    }

    return isJpgOrPng && isLt2M && limitNumber;
  };

  return (
    <div className="gallery-upload">
      <Dragger
        beforeUpload={beforeUploadImage}
        // method="PATCH"
        // action={`${BASE_URL}api/profiles/${userId}`}
        customRequest={uploadGalleryImages}
        showUploadList={false}
        listType="picture"
        defaultFileList={fileList}
        multiple={true}
        disabled={
          userGalleryImages && userGalleryImages?.length >= galleryPhotoLimit ? true : false
        }
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ color: '#f8b6b6' }} />
        </p>
        <p className="ant-upload-text">
          {userGalleryImages && userGalleryImages?.length >= galleryPhotoLimit
            ? 'Exceeded limit of 30 images'
            : 'Click or drag and drop an image'}
        </p>
      </Dragger>
      <div className="gallery-images-container">
        {isLoading ? (
          <div className="gallery-loader">
            <LoaderComponent />
          </div>
        ) : null}

        <Image.PreviewGroup>
          <ResponsiveMasonry columnsCountBreakPoints={{ 350: 2, 750: 2, 900: 3 }}>
            <Masonry gutter="10px">
              {userGalleryImages?.map((image) => {
                return (
                  <div className="gallery-image-container" key={image.id}>
                    <Image src={image.thumbnail_url} />
                    <div className="delete-image-container" onClick={() => showModal(image.name)}>
                      <DeleteOutlined />
                    </div>
                  </div>
                );
              })}
            </Masonry>
          </ResponsiveMasonry>
        </Image.PreviewGroup>
      </div>
      <Modal
        className="delete-image-modal"
        footer={null}
        title="Delete Photo"
        open={isModalOpen}
        onCancel={handleCancel}
      >
        <p>Are You sure You want to delete this photo? </p>
        <div className="modal-button-container">
          <Button className="SubmitButton" onClick={() => handleCancel()} text="No" />
          <Button className="SubmitButton" onClick={() => handleOk()} text="Yes" />
        </div>
      </Modal>
    </div>
  );
};

export default GalleryUpload;
